import { defineSlotRecipe } from '@chakra-ui/react';

/*
 * Design system source: .claude/skills/monetizenow-design/preview/badges.html
 *
 * 2px radius, 2px 8px padding, 11px font, medium (500) weight, 20px height.
 * Color pairs preserved from legacy but retuned for the new fg/bg token pairs.
 *
 * v3 port notes:
 * - Slots renamed from v2's `tagAnatomy`: `container` → `root`, `closeButton`
 *   → `closeTrigger`. `label` is unchanged. v3 adds `startElement`/
 *   `endElement` slots (replacing v2's `icon` used by both TagLeftIcon and
 *   TagRightIcon) — this app has zero real consumers of either icon slot
 *   (confirmed via repo-wide grep for `<MTagLeftIcon`/`<MTagRightIcon`), so
 *   they're left at base defaults rather than guessing at a style.
 * - Chakra's built-in `tag` recipe's `size` variant (default `md`) bleeds
 *   `paddingInline`/`minHeight`/`gap` onto `root` and `fontSize`/`lineHeight`
 *   (via `textStyle`) onto `label` even though this app never sets a `size`
 *   prop — confirmed empirically via `createSystem`/`getSlotRecipeFn`, same
 *   technique as Card/Menu/Tabs/Popover. Every key below restates what the
 *   built-in would otherwise inject so none of it leaks through.
 * - The v2 `outline` variant used to spread a computed function-variant off
 *   `@chakra-ui/theme`'s own default `Tag.variants.outline` before applying
 *   overrides — that package doesn't exist in the app's dependency tree at
 *   all anymore (v3's dependency bump dropped it outright), and every value
 *   it contributed was fully overridden by this file's own literal values
 *   regardless, so the port just keeps the literal result directly.
 */

const neutralizeBuiltInSizing = {
  root: {
    paddingInline: '8px',
    minHeight: '20px',
    gap: 0,
  },
  label: {
    fontSize: '11px',
    lineHeight: '16px',
  },
};

export const tagTheme = defineSlotRecipe({
  slots: ['root', 'label', 'closeTrigger', 'startElement', 'endElement'],
  base: {
    root: {
      ...neutralizeBuiltInSizing.root,
      fontWeight: 500,
      borderRadius: 'xs', // 2px
      fontSize: '11px', // design-system literal — tighter than `xs` (12px)
      lineHeight: '16px',
      h: '20px',
      px: '8px',
      py: '2px',
    },
    label: neutralizeBuiltInSizing.label,
    closeTrigger: {
      h: '18px',
      w: '18px',
      borderRadius: 'sm',
      opacity: 1,
      // v3's preflight no longer gives buttons `cursor: pointer`.
      cursor: 'pointer',
      // Hover reads like a standard icon button — light tint, not the
      // Chakra default (which only changes opacity). `blue`/`lightBlue` also
      // darken the icon, below.
      _hover: {
        bg: 'bg.hover',
        opacity: 1,
      },
      // Chakra's default TagCloseButton signals focus by swapping its
      // background to a translucent dark fill (`rgba(0,0,0,0.14)`) AND
      // applying the wrong outline shadow. Replace with the indigo halo
      // and explicitly clear the bg so focus doesn't read as pressed.
      _focusVisible: {
        bg: 'transparent',
        boxShadow: 'focus',
        outline: 'none',
      },
    },
  },
  variants: {
    variant: {
      blue: {
        root: { color: 'tIndigo.base', bgColor: 'tBlue.hover' },
        closeTrigger: { _hover: { color: 'tIndigo.700' } },
      },
      lightBlue: {
        root: { color: 'tIndigo.base', bgColor: 'tBlue.hover' },
        closeTrigger: { _hover: { color: 'tIndigo.700' } },
      },
      gray: { root: { color: 'tGray.darkPurple', bgColor: 'tGray.back' } },
      green: { root: { color: 'tGreen.mDarkShade', bgColor: 'tGreen.light' } },
      greenText: {
        root: { color: 'tGreen.mDarkShade', bgColor: 'transparent' },
      },
      neutral: { root: { color: 'fg.muted', bgColor: 'transparent' } },
      neutralItalic: {
        root: {
          color: 'fg.muted',
          bgColor: 'transparent',
          fontStyle: 'italic',
        },
      },
      orange: { root: { color: 'tOrange.base', bgColor: 'tCreamsicle.base' } },
      purple: { root: { color: 'tMagenta.base', bgColor: 'tMagenta.50' } },
      red: { root: { color: 'tRed.base', bgColor: 'tRed.cosmos' } },
      operand: {
        root: {
          p: 0,
          minW: '34px',
          textAlign: 'center',
          borderRadius: 'xs',
          fontSize: 'xs',
          fontWeight: 600,
          textTransform: 'uppercase',
          color: 'fg.strong',
          bg: 'tBlue.hover',
          justifyContent: 'center',
        },
      },
      mileStone: {
        root: {
          w: '100%',
          flexGrow: 1,
          flexShrink: 0,
          maxWidth: 'fit-content',
          color: 'fg.muted',
          whiteSpace: 'nowrap',
          borderRadius: 'xl', // pill shape for milestones (design system pill example)
          border: '1px solid',
          borderColor: 'border.subtle',
          bg: 'transparent',
        },
      },
      upload: {
        root: { background: 'bg.muted' },
        label: { textDecoration: 'underline' },
      },
      outline: {
        root: {
          color: 'fg.primary',
          bg: 'bg.surface',
          shadow: 'none',
          border: '1px solid',
          borderColor: 'border.subtle',
        },
      },
    },
    size: {
      xs: {
        root: {
          minH: '20px',
          h: '20px',
          px: '8px',
        },
      },
    },
  },
  defaultVariants: {
    size: 'xs',
    variant: 'gray',
  },
});

export default tagTheme;
